import { PublishPanel } from '@/components/campaigns/publish-panel';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import {
  useCampaign,
  useExperience,
  useExperiences,
  useSegments,
  useUpdateCampaign,
  useUpdateExperience,
  type Campaign,
} from '@/lib/queries';
import { cn } from '@/lib/cn';
import { useContextStore } from '@/stores/context';
import { slotSchema } from '@personalizehq/schemas';
import { Link, createFileRoute } from '@tanstack/react-router';
import { ChevronLeft } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { StatusBadge } from './campaigns.index';

export const Route = createFileRoute('/_authed/campaigns/$id')({
  component: CampaignDetail,
});

// Radix Select forbids an empty-string item value, so "no link" needs a sentinel. Null on a draft
// is legal — publish is where the link becomes law.
const NONE = '__none__';

const DEFAULT_CAP = { session_limit: 1, cross_session_limit: 3, window_days: 7 };

/** `<input type="datetime-local">` speaks local wall-clock; the wire speaks ISO-8601 UTC. */
function isoToLocal(iso: string | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function localToIso(local: string): string | undefined {
  return local ? new Date(local).toISOString() : undefined;
}

function CampaignDetail(): React.JSX.Element {
  const { id } = Route.useParams();
  const { data: campaign, isLoading } = useCampaign(id);

  if (isLoading || !campaign) return <Skeleton className="h-96 w-full" />;
  // Remount on load so every field's initial state comes from the fetched campaign, not from a
  // render that ran while it was undefined.
  return <CampaignForm key={campaign.id} campaign={campaign} />;
}

function CampaignForm({ campaign }: { campaign: Campaign }): React.JSX.Element {
  const { appId } = useContextStore();
  const update = useUpdateCampaign(campaign.id, appId);
  const updateExperience = useUpdateExperience(campaign.experience_id ?? '', appId);
  const { data: experiences } = useExperiences(appId);
  const { data: segments } = useSegments(appId);

  const [name, setName] = useState(campaign.name);
  const [status, setStatus] = useState<Campaign['status']>(campaign.status);
  const [priority, setPriority] = useState<Campaign['priority']>(campaign.priority);
  const [experienceId, setExperienceId] = useState(campaign.experience_id ?? NONE);
  const [segmentId, setSegmentId] = useState(campaign.segment_id ?? NONE);

  const placement = campaign.placement ?? { kind: 'overlay' };
  const [inline, setInline] = useState(placement.kind === 'inline');
  const [slot, setSlot] = useState(placement.kind === 'inline' ? placement.slot : '');

  const [triggerType, setTriggerType] = useState(campaign.trigger_config?.type ?? 'always');
  const [event, setEvent] = useState(
    campaign.trigger_config?.type === 'on_event' ? campaign.trigger_config.event : ''
  );
  const [screen, setScreen] = useState(
    campaign.trigger_config?.type === 'on_screen' ? campaign.trigger_config.screen : ''
  );

  const [start, setStart] = useState(isoToLocal(campaign.schedule?.start));
  const [end, setEnd] = useState(isoToLocal(campaign.schedule?.end));

  const [capOn, setCapOn] = useState(campaign.frequency_cap !== null);
  const [cap, setCap] = useState(campaign.frequency_cap ?? DEFAULT_CAP);

  // PLACEMENT AND SURFACE MUST AGREE, and the schema says so in three ways at once (bundle.ts): an
  // inline placement needs EVERY step to be an inline surface, and exactly ONE step ("a widget is
  // not a flow"); an inline step forbids an overlay placement. Its own comment reads *"both are one
  // dropdown away"* — and these are the two dropdowns it was talking about.
  //
  // PLACEMENT DRIVES. The campaign says WHERE, so choosing `inline` here is the author's intent, and
  // the experience is made to match rather than the choice being second-guessed. What we will NOT do
  // is let both fields wander and reject the pair at publish: an illegal state you can reach and
  // then be told off for is a worse editor than one you cannot reach.
  const linked = useExperience(experienceId === NONE ? '' : experienceId);
  const linkedDoc = experienceId === NONE ? undefined : linked.data?.document;
  const steps = linkedDoc?.steps ?? [];

  const mismatch: string | null = !linkedDoc
    ? null
    : inline && steps.length !== 1
      ? `“${linked.data?.name}” has ${steps.length} steps — an inline experience is a single step: a slot in someone else’s screen is not a flow.`
      : inline && steps.some((s) => s.surface !== 'inline')
        ? `“${linked.data?.name}” is a ${steps[0]?.surface} experience. An inline placement needs an inline surface — it has no window to render in.`
        : !inline && steps.some((s) => s.surface === 'inline')
          ? `“${linked.data?.name}” is an inline experience: it needs an inline placement and a slot, or it renders in a floating window with no chrome.`
          : null;

  /** The fix, applied rather than described — but only when it is UNAMBIGUOUS. A 3-step flow cannot
   *  become a widget by deleting two steps on the author's behalf; that is a decision, not a repair. */
  const repairable = Boolean(linkedDoc) && steps.length === 1;
  const repair = (): void => {
    if (!linkedDoc || !repairable) return;
    updateExperience.mutate(
      {
        document: {
          ...linkedDoc,
          steps: linkedDoc.steps.map((s) => ({ ...s, surface: inline ? ('inline' as const) : ('modal' as const) })),
        },
      },
      {
        onSuccess: () => toast.success(inline ? 'Experience is now an inline widget.' : 'Experience is now a modal.'),
        onError: (error: Error) => toast.error(error.message),
      }
    );
  };

  function buildTrigger(): Campaign['trigger_config'] {
    if (triggerType === 'on_event') return { type: 'on_event', event };
    if (triggerType === 'on_screen') return { type: 'on_screen', screen };
    return { type: 'always' };
  }

  function save(): void {
    update.mutate(
      {
        name: name.trim(),
        status,
        priority,
        experience_id: experienceId === NONE ? null : experienceId,
        segment_id: segmentId === NONE ? null : segmentId,
        placement: inline ? { kind: 'inline' as const, slot: slot.trim() } : { kind: 'overlay' as const },
        trigger_config: buildTrigger(),
        schedule: start ? { start: localToIso(start)!, ...(end && { end: localToIso(end)! }) } : null,
        frequency_cap: capOn ? cap : null,
      },
      {
        onSuccess: () => toast.success('Saved.'),
        onError: (error: Error) => toast.error(error.message),
      }
    );
  }

  // The slot is a CONTRACT WITH THE HOST ENGINEER — the string they wrote in
  // `PHQInlineView(slot: "home_hero")`. A typo here is a widget that renders nowhere and reports
  // nothing, so the charset (`slotSchema`) is enforced at the field rather than discovered at
  // publish.
  const slotError =
    inline && !slotSchema.safeParse(slot.trim()).success
      ? slot.trim() === ''
        ? 'An inline campaign needs a slot — it is the id the app asks for.'
        : 'Lowercase letters, digits and underscores only.'
      : null;

  const invalid =
    !name.trim() ||
    slotError !== null ||
    mismatch !== null ||
    (triggerType === 'on_event' && !event.trim()) ||
    (triggerType === 'on_screen' && !screen.trim()) ||
    (capOn && (cap.window_days < 1 || cap.window_days > 90));

  return (
    <div className="space-y-6">
      <div>
        <Link
          to="/campaigns"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="size-4" />
          Campaigns
        </Link>
        <div className="mt-2 flex items-center gap-3">
          <h1 className="text-xl font-semibold tracking-tight">{campaign.name}</h1>
          <StatusBadge status={campaign.status} />
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Settings</CardTitle>
          <CardDescription>Changes apply on save. Publishing is separate and explicit.</CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input id="name" value={name} onChange={(e) => setName(e.target.value)} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              {/* Archive is a status, not a DELETE — the API has no delete for campaigns. */}
              <Select value={status} onValueChange={(v) => setStatus(v as Campaign['status'])}>
                <SelectTrigger id="status" className="capitalize">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="paused">Paused</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="priority">Priority</Label>
              <Select value={priority} onValueChange={(v) => setPriority(v as Campaign['priority'])}>
                <SelectTrigger id="priority">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="passive">Passive</SelectItem>
                  <SelectItem value="time_sensitive">Time-sensitive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="experience">Experience</Label>
              <Select value={experienceId} onValueChange={setExperienceId}>
                <SelectTrigger id="experience">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>Not linked</SelectItem>
                  {experiences?.map((experience) => (
                    <SelectItem key={experience.id} value={experience.id}>
                      {experience.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* WHERE IT RENDERS (B3). Never had a control; `inline` is a v1 requirement. */}
            <div className="space-y-2">
              <Label htmlFor="placement">Placement</Label>
              <Select
                value={inline ? 'inline' : 'overlay'}
                onValueChange={(v) => setInline(v === 'inline')}
              >
                <SelectTrigger id="placement">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="overlay">Overlay — a modal, sheet or banner over the app</SelectItem>
                  <SelectItem value="inline">Inline — a widget in a slot in the app&rsquo;s own screen</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                An overlay owns the screen. An inline widget lives in a slot the host app draws with{' '}
                <code>PHQInlineView</code>.
              </p>
            </div>

            {inline ? (
              <div className="space-y-2">
                <Label htmlFor="slot">Slot</Label>
                <Input
                  id="slot"
                  value={slot}
                  placeholder="home_hero"
                  onChange={(e) => setSlot(e.target.value)}
                  aria-invalid={slotError !== null}
                />
                <p className={cn('text-xs', slotError ? 'text-destructive' : 'text-muted-foreground')}>
                  {slotError ??
                    'The id the app asks for. It must match the string the host engineer passed to PHQInlineView.'}
                </p>
              </div>
            ) : null}

            {mismatch ? (
              <div className="space-y-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-3">
                <p className="text-xs text-amber-600 dark:text-amber-500">{mismatch}</p>
                {repairable ? (
                  <Button type="button" size="sm" variant="outline" onClick={repair} disabled={updateExperience.isPending}>
                    {inline ? 'Make the experience inline' : 'Make the experience a modal'}
                  </Button>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Delete the extra steps in the builder first — collapsing a flow into a widget is a
                    decision, not a repair.
                  </p>
                )}
              </div>
            ) : null}

            <div className="space-y-2">
              <Label htmlFor="segment">Segment</Label>
              <Select value={segmentId} onValueChange={setSegmentId}>
                <SelectTrigger id="segment">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>Not linked</SelectItem>
                  {segments?.map((segment) => (
                    <SelectItem key={segment.id} value={segment.id}>
                      {segment.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="trigger">Trigger</Label>
              <Select
                value={triggerType}
                onValueChange={(v) => setTriggerType(v as NonNullable<Campaign['trigger_config']>['type'])}
              >
                <SelectTrigger id="trigger">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="always">On app launch</SelectItem>
                  <SelectItem value="on_event">On event</SelectItem>
                  <SelectItem value="on_screen">On screen</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {triggerType === 'on_event' && (
              <div className="space-y-2">
                <Label htmlFor="event">Event name</Label>
                <Input
                  id="event"
                  value={event}
                  onChange={(e) => setEvent(e.target.value)}
                  placeholder="checkout_completed"
                />
              </div>
            )}

            {triggerType === 'on_screen' && (
              <div className="space-y-2">
                <Label htmlFor="screen">Screen name</Label>
                <Input
                  id="screen"
                  value={screen}
                  onChange={(e) => setScreen(e.target.value)}
                  placeholder="HomeScreen"
                />
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label>Schedule</Label>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <span className="text-xs text-muted-foreground">Starts</span>
                <Input type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} />
              </div>
              <div className="space-y-1">
                <span className="text-xs text-muted-foreground">Ends (optional)</span>
                <Input
                  type="datetime-local"
                  value={end}
                  onChange={(e) => setEnd(e.target.value)}
                  disabled={!start}
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Local time, stored as UTC. Leave the start empty to run with no schedule.
            </p>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <Switch id="cap" checked={capOn} onCheckedChange={setCapOn} />
              <Label htmlFor="cap">Frequency cap</Label>
            </div>
            {capOn && (
              <div className="grid grid-cols-3 gap-4 pt-1">
                <div className="space-y-1">
                  <span className="text-xs text-muted-foreground">Per session</span>
                  <Input
                    type="number"
                    min={0}
                    value={cap.session_limit}
                    onChange={(e) => setCap({ ...cap, session_limit: Number(e.target.value) })}
                  />
                </div>
                <div className="space-y-1">
                  <span className="text-xs text-muted-foreground">Across sessions</span>
                  <Input
                    type="number"
                    min={0}
                    value={cap.cross_session_limit}
                    onChange={(e) => setCap({ ...cap, cross_session_limit: Number(e.target.value) })}
                  />
                </div>
                <div className="space-y-1">
                  <span className="text-xs text-muted-foreground">Window (days, max 90)</span>
                  <Input
                    type="number"
                    min={1}
                    max={90}
                    value={cap.window_days}
                    onChange={(e) => setCap({ ...cap, window_days: Number(e.target.value) })}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end">
            <Button onClick={save} disabled={invalid || update.isPending}>
              {update.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <PublishPanel campaign={campaign} appId={appId} />
    </div>
  );
}
